import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  isRejoiningAtom,
  isRejoinPromptOpenAtom,
} from "../../video-sdk/core/atoms";
import { useRejoinRoom as useBaseRejoinRoom } from "../../video-sdk/actions/useLifecycleActions";
import { RejoinModalViewModel } from "./types";

import { RoomDelegator } from "../../video-sdk/core/room/RoomDelegator";
import { GenericRoomError } from "../../video-sdk/core/utils";
import { useCallback } from "react";
import { TrackingEvents } from "../../events";
import { useAppDependenciesContext } from "../../state/useAppDependenciesContext";
import { AppDependencies } from "../../state";
import {
  useRejoinModalTrackingEvents as useBaseRejoinModalTrackingEvents,
  UseRejoinModalTrackingEventsOptions,
} from "./useRejoinModalTrackingEvents";
import { validateRoomHasParticipants } from "../../video-sdk/core/room/validators";
import { RoomValidationError } from "../../video-sdk/core/room/validators/RoomValidationError";

export const REJOIN_ERROR_TOAST_TAG = "rejoin-error";
export const REJOIN_ERROR_TOAST_TAGS = new Set([REJOIN_ERROR_TOAST_TAG]);

export type RejoinError = GenericRoomError | RoomValidationError | Error;

function showErrorToast(toast: AppDependencies["toast"]) {
  toast.error("Unable to connect. Please try again.", {
    purpose: "system",
    tags: REJOIN_ERROR_TOAST_TAGS,
  });
}

type UseBaseRejoinModalViewModelResult = Omit<
  RejoinModalViewModel,
  "onJoin"
> & {
  rejoin: (options?: {
    /**
     * Called when rejoining fails with an unrecoverable error e.g. room no
     * longer exists, missing provider, etc.
     */
    onTerminalFailure?: (error: RejoinError) => void;
  }) => Promise<RoomDelegator | void>;
  closeModal: () => void;
};

function logRejoinEvent(
  sendLoggingEvents: AppDependencies["sendLoggingEvents"],
) {
  sendLoggingEvents(
    TrackingEvents.ROOM_REJOINING,
    {},
    {
      logLevel: "info",
      message: "Attempting to rejoin room",
    },
  );
}

function logRejoinFailure(
  error: RejoinError,
  isTerminalFailure: boolean,
  logger: AppDependencies["logger"],
) {
  logger.error("Failed to rejoin session", { isTerminalFailure }, error);
}

export type UseBaseRejoinModalViewModelOptions = {
  /**
   * Dependency injection primarily used for testing. Defaults to the actual
   * {@link useBaseRejoinRoom} lifecycle action hook.
   */
  useRejoinRoom?: typeof useBaseRejoinRoom;
  useRejoinModalTrackingEvents?: typeof useBaseRejoinModalTrackingEvents;
} & UseRejoinModalTrackingEventsOptions;

export function useBaseRejoinModalViewModel({
  useRejoinRoom = useBaseRejoinRoom,
  useRejoinModalTrackingEvents = useBaseRejoinModalTrackingEvents,
  appointmentShortId,
}: UseBaseRejoinModalViewModelOptions = {}): UseBaseRejoinModalViewModelResult {
  const { logger, toast, sendLoggingEvents } = useAppDependenciesContext();
  const isOpen = useAtomValue(isRejoinPromptOpenAtom);
  const [isRejoining, setIsRejoining] = useAtom(isRejoiningAtom);
  const setIsRejoinPromptOpen = useSetAtom(isRejoinPromptOpenAtom);
  const closeModal = useCallback(
    () => setIsRejoinPromptOpen(false),
    [setIsRejoinPromptOpen],
  );
  const rejoinRoom = useRejoinRoom();

  const { trackRejoinClicked } = useRejoinModalTrackingEvents({
    appointmentShortId,
  });

  const rejoin: UseBaseRejoinModalViewModelResult["rejoin"] = useCallback(
    async ({ onTerminalFailure: handleTerminalFailure } = {}) => {
      setIsRejoining(true);
      logRejoinEvent(sendLoggingEvents);
      let rejoinRerror: RejoinError | undefined;
      return rejoinRoom({
        roomValidators: [validateRoomHasParticipants],
      })
        .then((room) => {
          toast.closeByTag(REJOIN_ERROR_TOAST_TAG);
          closeModal();
          return room;
        })
        .catch((error: RejoinError) => {
          const canRetry =
            error instanceof GenericRoomError &&
            error.kind === "connection-error";
          const isTerminalFailure = !canRetry;

          logRejoinFailure(error, isTerminalFailure, logger);

          if (isTerminalFailure) {
            handleTerminalFailure?.(error);
            return;
          }

          showErrorToast(toast);
        })
        .finally(() => {
          setIsRejoining(false);
          trackRejoinClicked(rejoinRerror);
        });
    },
    [
      closeModal,
      logger,
      rejoinRoom,
      sendLoggingEvents,
      setIsRejoining,
      toast,
      trackRejoinClicked,
    ],
  );

  return {
    isOpen,
    isLoading: isRejoining,
    closeModal,
    rejoin,
  };
}
